/**
 * The pure half of the route composition.
 *
 * `./index.ts` holds the two `import.meta.glob` calls, because a glob is
 * resolved by Vite at build time from the literal it is written with and cannot
 * be passed in. Everything it then does with the result lives here, where it
 * can be called with a hand-built module map and tested for the cases a glob
 * over the real filesystem cannot produce on demand: an area whose `bundle`
 * export is missing, an auth module that has not landed, two areas discovered
 * in an arbitrary filesystem order.
 */

import { createElement, type FunctionComponent, type ReactNode } from 'react';

import { RequireCapability } from '../shell/RequireCapability';
import type { NavEntry, RouteBundle, RouteModule } from './contract';

function isRouteBundle(value: unknown): value is RouteBundle {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as { routes?: unknown; nav?: unknown };
  return Array.isArray(candidate.routes) && Array.isArray(candidate.nav);
}

/**
 * Collects the `bundle` export of every discovered feature area.
 *
 * Sorted by module path so a route conflict (two areas declaring the same
 * path) is reported in a deterministic order and not one that reshuffles
 * between machines. Navigation order no longer rides on this sort -- see
 * `sortByOrder` below.
 *
 * Throws, rather than skipping, when a discovered module does not export a
 * usable `bundle`. A file at `src/app/<area>/routes.ts` exists because somebody
 * created it in order to register an area; silently ignoring a typo in its
 * export name would present as "my routes did not appear" with nothing to read.
 */
export function bundlesFrom(modules: Record<string, unknown>): RouteBundle[] {
  return Object.keys(modules)
    .sort()
    .map((path) => {
      const candidate = (modules[path] as { bundle?: unknown } | undefined)?.bundle;
      if (!isRouteBundle(candidate)) {
        throw new Error(
          `${path} was discovered as a feature area but exports no \`bundle: RouteBundle\``,
        );
      }
      return candidate;
    });
}

/**
 * A nav group's entries, ordered by `NavEntry.order` rather than by which
 * area or module discovery happened to visit first. Every producer across
 * every feature area sets `order`, so an entry's place is a decision made
 * once at its declaration, not an accident of the import graph -- the
 * composition applies it after concatenating every area's contribution,
 * which is what lets one campaign screen's entry sort next to another
 * area's without either area importing the other.
 */
export function sortByOrder(entries: NavEntry[]): NavEntry[] {
  return [...entries].sort((a, b) => a.order - b.order);
}

/**
 * The named export of a discovered module, or a thrown error naming what was
 * missing.
 *
 * Symmetric with `bundlesFrom` above, and for the same reason. This resolved
 * the three auth route components while they were another task's unlanded
 * files, and returned a "this page is not available" placeholder when one was
 * absent -- correct then, wrong now that all three have landed: the only way
 * to reach the placeholder today is for a producer to rename or delete an
 * export, and the honest report of that is a failure at boot, not a route
 * mounted at `/sign-up` that renders an apology. A silent fallback here
 * also passes every existing test, because a test comparing the mounted
 * component against the globbed export agrees with itself when the export is
 * gone.
 */
export function requireComponent(
  modules: Record<string, unknown>,
  path: string,
  exportName: string,
): FunctionComponent {
  const exported = (modules[path] as Record<string, unknown> | undefined)?.[exportName];
  if (typeof exported !== 'function') {
    throw new Error(`${path} exports no \`${exportName}\` component`);
  }
  return exported as FunctionComponent;
}

/**
 * Wraps a route's element in its capability guard.
 *
 * Applied by the composition to every route that declares one, so an area
 * cannot forget it: declaring `requires` is the whole act of gating, and there
 * is no second step an author could skip.
 */
export function guarded(route: RouteModule): ReactNode {
  if (route.requires === undefined) return route.element;
  return createElement(RequireCapability, {
    capability: route.requires,
    children: route.element,
  });
}
